// PrivateRelationsSection.tsx — Verknüpfungen der PERSONEN-Anlage, inline:
//   (1) Haushalt  (2) Familienstand  (3) Beziehungen außerhalb des Haushaltes
//   (4) Verbindung zu Unternehmen    (5) Kundenverbindung
// Voll über formFields gesteuert (rel_household/rel_weitere/rel_personFirma/
// rel_kvGroupId/familyStatus/weddingDateCivil/weddingDateChurch).
// Persistiert wird NICHTS hier — erst beim Speichern (persistCollectedRelations).

import * as React from "react";
import { SectionCard } from "../forms/formPrimitives";
import { HouseholdList } from "./HouseholdList";
import { PersonFirmaList } from "./PersonFirmaList";
import { KundenverbindungSelect } from "./KundenverbindungSelect";
import { FamilienstandSection, type HochzeitsDateiItem } from "./FamilienstandSection";
import { PersonalAssistantSection } from "./PersonalAssistantSection";
import type { HouseholdItem, PersonFirmaItem, AssistantItem } from "./types";
import { AnlageOrganigramm, BeziehungsDiagramm, DiagrammKnopf, type AnlageBeziehungen } from "./AnlageDiagramme";
import type { EintragsListeSteuerung } from "../forms/EintragsListe";
import { RELATIONSHIP_FREE, genderOf } from "../../utils/householdRole";
import {
  familienstandAbleiten,
  familienstandAusKanten,
  type FamilienstandKante,
} from "../../utils/familienstandAbleiten";
import { hatLebendenPartner, partnerErfassenFragen, partnerFrageFuerStatus, partnerImHaushalt } from "./partnerErfassen";
import { useAkteSofortSpeichern } from "./akteSofortSpeichern";
import { PersonAufFirmaDialog, personAufFirmaPruefen } from "./PersonAufFirmaDialog";
import { hinweisen } from "../redesign/bestaetigen";
import type { ContactRef } from "./types";

const parseArr = (raw: unknown): any[] => {
  if (typeof raw !== "string" || !raw.trim()) return [];
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
};
const str = (v: unknown): string => (typeof v === "string" ? v : "");

/**
 * Partner-Kanten der Anlage für die Familienstand-Ableitung (T3). Das Label
 * eines Eintrags sagt „was die andere Person für diesen Kontakt ist" — bei
 * Freitext steht es in `freeRole`. Gegenüber ohne Kontakt-id (frei
 * eingetippt) zählen über die Eintrags-id.
 */
export const familienstandKantenAus = (items: HouseholdItem[]): FamilienstandKante[] =>
  items
    .filter((it) => !!it.ref)
    .map((it) => ({
      partnerId: it.ref?.contactId ?? it.id,
      label: it.relationLabel === RELATIONSHIP_FREE ? (it.freeRole ?? "").trim() : it.relationLabel,
      partnerVerstorben: !!it.ref?.deceased,
    }));

export function PrivateRelationsSection({
  formFields,
  updateField,
  existingHousehold,
  existingWeitere,
  existingPersonFirma,
  existingKv,
  existingAssistenz,
  hochzeitDateien,
  onAddHochzeitDateien,
  onRemoveHochzeitDatei,
  selbstId,
}: {
  /** Id der Person in der Akte (Bearbeiten-Modus) — damit das Organigramm sie nicht doppelt zeigt. */
  selbstId?: number;
  formFields: Record<string, string | number | boolean>;
  updateField: (name: string, value: string) => void;
  /** Optional: read-only Anzeige BESTEHENDER Verbindungen, oben in der jeweiligen
   *  Karte eingebettet (z. B. in der Altkunden-Bereinigung). In der normalen
   *  Anlage nicht gesetzt → die Karten bleiben reine Neuanlage. */
  existingHousehold?: React.ReactNode;
  existingWeitere?: React.ReactNode;
  existingPersonFirma?: React.ReactNode;
  existingKv?: React.ReactNode;
  existingAssistenz?: React.ReactNode;
  hochzeitDateien?: HochzeitsDateiItem[];
  onAddHochzeitDateien?: (files: File[]) => void;
  onRemoveHochzeitDatei?: (id: string) => void;
}) {
  const setKey = (key: string, arr: unknown[]) => updateField(key, JSON.stringify(arr));

  // Diagramme: Stand beim Öffnen einfrieren — das Bild soll nicht bei jedem
  // Neurendern des Formulars neu rechnen oder nachladen.
  const [diagramm, setDiagramm] = React.useState<{ art: "beziehungen" | "organigramm"; daten: AnlageBeziehungen } | null>(null);
  const oeffne = (art: "beziehungen" | "organigramm") =>
    setDiagramm({
      art,
      daten: {
        selbstId,
        selbstName: [str(formFields.firstname), str(formFields.lastname)].filter(Boolean).join(" "),
        selbstTyp: "Person",
        selbstAnrede: str(formFields.anrede),
        selbstGeburtsdatum: str(formFields.dateofbirth),
        selbstVerstorben: str(formFields.deceased) === "ja",
        haushalt: parseArr(formFields.rel_household) as HouseholdItem[],
        weitere: parseArr(formFields.rel_weitere) as HouseholdItem[],
        personFirma: parseArr(formFields.rel_personFirma) as PersonFirmaItem[],
      },
    });
  // Alle Diagramm-Knöpfe heißen „Organigramm" (User 26.09.2026).
  const beziehungsKnopf = <DiagrammKnopf text="Organigramm" onClick={() => oeffne("beziehungen")} />;

  const householdItems = parseArr(formFields.rel_household) as HouseholdItem[];
  const weitereItems = parseArr(formFields.rel_weitere) as HouseholdItem[];

  // Familienstand aus den Partner-Beziehungen (T3, gleiche Regel wie das
  // Backend: utils/familienstandAbleiten). Gesetzt wird NUR auf ein leeres
  // oder „Ledig"-Feld — eine bewusste Auswahl überschreibt die Ableitung nie.
  // Gespeichert wird `familyStatusOther || familyStatus`.
  const familienstandJetzt = String(formFields.familyStatusOther || formFields.familyStatus || "");
  const abgeleitet = familienstandAusKanten(familienstandKantenAus([...householdItems, ...weitereItems]));
  const hatEhepersonInHousehold = abgeleitet === "Verheiratet";

  const [forceOpenHochzeit, setForceOpenHochzeit] = React.useState(hatEhepersonInHousehold);

  const ableiten = (haushalt: HouseholdItem[], weitere: HouseholdItem[]) => {
    const kanten = familienstandKantenAus([...haushalt, ...weitere]);
    const neu = familienstandAbleiten(familienstandJetzt, kanten);
    if (neu) {
      updateField("familyStatus", neu);
      updateField("familyStatusOther", "");
    }
    if ((neu ?? familienstandJetzt) === "Verheiratet" && familienstandAusKanten(kanten) === "Verheiratet") {
      setForceOpenHochzeit(true);
    }
  };

  // Auch beim Öffnen und bei Änderungen an anderer Stelle (Folgefelder im
  // Status-Schritt, Umwandeln mit vorhandenen Beziehungen).
  React.useEffect(() => {
    if (!abgeleitet) return;
    ableiten(householdItems, weitereItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abgeleitet]);

  const handleHouseholdChange = (nextHousehold: HouseholdItem[]) => {
    setKey("rel_household", nextHousehold);
    ableiten(nextHousehold, weitereItems);
  };

  const handleWeitereChange = (nextWeitere: HouseholdItem[]) => {
    setKey("rel_weitere", nextWeitere);
    ableiten(householdItems, nextWeitere);
  };

  // „Verheiratet"/„Zusammenlebend"/„Geschieden" gewählt, aber kein passender
  // Partner erfasst → Popup; auf Wunsch öffnet die Haushaltsliste (bzw. bei
  // Ex-Partnern die Liste außerhalb des Haushalts) einen vorbelegten Eintrag.
  // Überspringen lässt den Familienstand einfach stehen.
  const haushaltSteuerung = React.useRef<EintragsListeSteuerung<HouseholdItem> | null>(null);
  const weitereSteuerung = React.useRef<EintragsListeSteuerung<HouseholdItem> | null>(null);
  const beiFamilienstandAuswahl = async (v: string) => {
    const art = partnerFrageFuerStatus(v);
    if (!art) return;
    const beziehungen = familienstandKantenAus([...householdItems, ...weitereItems]).map((k) => ({
      label: k.label,
      verstorben: k.partnerVerstorben,
    }));
    if (hatLebendenPartner(art, beziehungen)) return;
    const label = await partnerErfassenFragen(
      art,
      genderOf({ gender: formFields.gender, salutationLabel: formFields.anrede }),
    );
    if (!label) return;
    // Ex-Partner gehören nicht in den Haushalt (02.10.2026).
    if (partnerImHaushalt(art)) haushaltSteuerung.current?.neu({ relationLabel: label, sameHousehold: true });
    else weitereSteuerung.current?.neu({ relationLabel: label, sameHousehold: false });
  };


  /**
   * Eine Person aus „Beziehungen außerhalb des Haushaltes" in den Haushalt
   * übernehmen (Ziehen & Ablegen oder Knopf, User 28.09.2026). Der Eintrag
   * behält id und Beziehungs-Ids — im Bearbeiten-Modus wird so die bestehende
   * Zeile auf `sameHousehold` umgestellt statt neu angelegt.
   */
  const inDenHaushalt = (id: string) => {
    const it = weitereItems.find((x) => x.id === id);
    if (!it) return;
    setKey("rel_weitere", weitereItems.filter((x) => x.id !== id));
    handleHouseholdChange([...householdItems, { ...it, sameHousehold: true }]);
  };

  /**
   * Gegenrichtung: eine Person aus dem Haushalt zu den weiteren Beziehungen.
   * Im Bearbeiten-Modus wird die bestehende Zeile nur auf
   * `sameHousehold=false` gestellt (PATCH), nicht neu angelegt.
   */
  const ausDemHaushalt = (id: string) => {
    const it = householdItems.find((x) => x.id === id);
    if (!it) return;
    handleHouseholdChange(householdItems.filter((x) => x.id !== id));
    handleWeitereChange([...weitereItems, { ...it, sameHousehold: false }]);
  };

  // Paket A7: Person aus Haushalt/Beziehungen auf eine verbundene Firma ziehen →
  // „Verantwortung fürs Unternehmen" für diese Person an dieser Firma, sofort
  // gespeichert. Nur in der Akte (dort gibt es den Sofort-Speichern-Kontext).
  const sofort = useAkteSofortSpeichern();
  const [personAufFirma, setPersonAufFirma] = React.useState<{ person: ContactRef; firma: ContactRef } | null>(null);
  const personAblegen = (firma: PersonFirmaItem, personEintragId: string) => {
    const person = [...householdItems, ...weitereItems].find((x) => x.id === personEintragId)?.ref;
    if (!person || !firma.ref) return;
    const grund = personAufFirmaPruefen(person, firma.ref, sofort);
    if (grund) {
      void hinweisen({ titel: "Verantwortung nicht möglich", text: grund });
      return;
    }
    setPersonAufFirma({ person, firma: firma.ref });
  };

  return (
    <>
      <SectionCard
        title="Personen die mit mir zusammen in einem Haushalt leben"
        collapsible
        defaultCollapsed={false}
        actions={beziehungsKnopf}
      >
        {existingHousehold}
        <HouseholdList
          value={householdItems}
          onChange={handleHouseholdChange}
          sameHousehold
          nachnameVorschlag={String(formFields.lastname ?? "").trim() || undefined}
          ablage={inDenHaushalt}
          ausDemHaushalt={ausDemHaushalt}
          steuerung={haushaltSteuerung}
        />
      </SectionCard>

      <SectionCard
        title="Familienstand"
        collapsible
        defaultCollapsed={false}
      >
        <FamilienstandSection
          formFields={formFields}
          updateField={updateField}
          forceOpenHochzeit={hatEhepersonInHousehold || forceOpenHochzeit}
          onAuswahl={(v) => void beiFamilienstandAuswahl(v)}
          hochzeitDateien={hochzeitDateien}
          onAddHochzeitDateien={onAddHochzeitDateien}
          onRemoveHochzeitDatei={onRemoveHochzeitDatei}
        />
      </SectionCard>

      <SectionCard
        title="Beziehungen außerhalb des Haushaltes"
        collapsible
        defaultCollapsed={false}
        actions={beziehungsKnopf}
      >
        {existingWeitere}
        <HouseholdList
          value={weitereItems}
          onChange={handleWeitereChange}
          sameHousehold={false}
          inDenHaushalt={inDenHaushalt}
          ablage={ausDemHaushalt}
          steuerung={weitereSteuerung}
        />
      </SectionCard>

      <SectionCard
        title="Persönlicher Assistent"
        collapsible
        defaultCollapsed
      >
        {existingAssistenz}
        <PersonalAssistantSection
          value={parseArr(formFields.rel_assistenz) as AssistantItem[]}
          onChange={(n) => setKey("rel_assistenz", n)}
        />
      </SectionCard>

      <SectionCard
        title="Verbindung zu Unternehmen"
        collapsible
        defaultCollapsed={false}
        actions={<DiagrammKnopf text="Organigramm" onClick={() => oeffne("organigramm")} />}
      >
        {existingPersonFirma}
        <PersonFirmaList
          value={parseArr(formFields.rel_personFirma) as PersonFirmaItem[]}
          onChange={(n) => setKey("rel_personFirma", n)}
          personAblegen={sofort ? personAblegen : undefined}
        />
      </SectionCard>

      <SectionCard
        title="Kundenverbindung"
        collapsible
        defaultCollapsed={false}
      >
        {existingKv}
        <KundenverbindungSelect
          value={str(formFields.rel_kvGroupId)}
          onChange={(v) => updateField("rel_kvGroupId", v)}
        />
      </SectionCard>

      {personAufFirma && sofort && (
        <PersonAufFirmaDialog
          person={personAufFirma.person}
          firma={personAufFirma.firma}
          sofort={sofort}
          onSchliessen={() => setPersonAufFirma(null)}
        />
      )}
      {diagramm?.art === "beziehungen" && (
        <BeziehungsDiagramm daten={diagramm.daten} onClose={() => setDiagramm(null)} />
      )}
      {diagramm?.art === "organigramm" && (
        <AnlageOrganigramm daten={diagramm.daten} onClose={() => setDiagramm(null)} />
      )}
    </>
  );
}
